import { useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, Download, Loader2, Maximize2, Package } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { OverviewSectionCard } from '@/components/agent/overview/OverviewSectionCard';
import { ActivationPackWithTarget } from '@/types/activation/activation-pack';
import { useAssetGenerator, useAddDesignsToPrintOrder } from '@/hooks/activation';
import { PosterDesign } from '@/types/activation/poster';
import { resolveLookbookBrand } from '@/components/activation/lookbook/lookbookBrand';
import { POSTER_DESIGNS } from '@/components/activation/poster/posterDesigns';
import { PosterPageFrame, POSTER_PAGE_W, POSTER_PAGE_H } from '@/components/activation/poster/PosterPageFrame';
import { PosterImmersiveViewer } from '@/components/activation/poster/PosterImmersiveViewer';

interface PackPostersSectionProps {
  pack: ActivationPackWithTarget;
  brand?: string;
}

const PREVIEW_W = 300;
const THUMB_W = 68;

/** A scaled, live render of one poster page (full A4). */
const ScaledPage = ({ width, children }: { width: number; children: React.ReactNode }) => {
  const scale = width / POSTER_PAGE_W;
  return (
    <div
      style={{ width, height: POSTER_PAGE_H * scale }}
      className="relative overflow-hidden rounded-xl bg-white shadow-[0_10px_30px_rgba(15,23,42,0.14)]"
    >
      <div style={{ position: 'absolute', top: 0, left: 0, transform: `scale(${scale})`, transformOrigin: 'top left' }}>
        {children}
      </div>
    </div>
  );
};

export const PackPostersSection = ({ pack, brand = '#4E9CFF' }: PackPostersSectionProps) => {
  const { downloadByType } = useAssetGenerator(pack);
  const { addDesigns, isAdding } = useAddDesignsToPrintOrder(pack, 'poster');
  const [isDownloading, setIsDownloading] = useState(false);
  const [active, setActive] = useState(0);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerMode, setViewerMode] = useState<'grid' | 'focus'>('grid');

  const posterBrand = useMemo(() => resolveLookbookBrand(pack), [pack]);
  const total = POSTER_DESIGNS.length;
  const design = POSTER_DESIGNS[active];

  const go = (dir: number) => setActive((i) => (i + dir + total) % total);

  const openImmersive = (mode: 'grid' | 'focus') => {
    setViewerMode(mode);
    setViewerOpen(true);
  };

  const handleDownload = async () => {
    if (isDownloading) return;
    setIsDownloading(true);
    try {
      await downloadByType('poster_pack_pdf');
      toast.success('Your posters are on their way');
    } catch {
      toast.error("Couldn't build your posters");
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <OverviewSectionCard
      title="Posters"
      subtitle="Standardized, print-ready posters for the wall, counter, or staff room — flip through, then download the branded PDF."
      genieIcon="files"
      brand={brand}
    >
      <div className="space-y-5">
        {/* Click-through gallery */}
        <div className="flex items-center justify-center gap-3 sm:gap-5">
          <button
            type="button"
            onClick={() => go(-1)}
            aria-label="Previous design"
            className="hidden sm:flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-600 hover:text-gray-900 hover:border-gray-300 transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>

          <button
            type="button"
            onClick={() => openImmersive('focus')}
            aria-label={`Open ${design.name} full-screen`}
            className="group relative rounded-xl"
          >
            <ScaledPage width={PREVIEW_W}>
              <PosterPageFrame brand={posterBrand} design={design} />
            </ScaledPage>
            <span className="pointer-events-none absolute inset-0 rounded-xl ring-2 ring-genie-primary/0 transition-all duration-300 group-hover:ring-genie-primary/50" />
            <span className="pointer-events-none absolute bottom-3 right-3 inline-flex items-center gap-1.5 rounded-lg bg-white/95 px-2.5 py-1.5 text-xs font-semibold text-gray-800 opacity-0 shadow-md transition-opacity duration-300 group-hover:opacity-100">
              <Maximize2 className="h-3.5 w-3.5" />
              Expand
            </span>
          </button>

          <button
            type="button"
            onClick={() => go(1)}
            aria-label="Next design"
            className="hidden sm:flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-600 hover:text-gray-900 hover:border-gray-300 transition-colors"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>

        {/* Current design label + position */}
        <div className="text-center">
          <div className="text-sm font-semibold text-gray-900">
            {design.name}
            <span className="text-gray-400 font-normal"> · {active + 1} of {total}</span>
          </div>
          <div className="text-xs text-gray-500 mt-0.5">{design.meta}</div>
        </div>

        {/* Thumbnail strip */}
        <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
          {POSTER_DESIGNS.map((d, i) => (
            <button
              key={d.id}
              type="button"
              onClick={() => setActive(i)}
              aria-label={d.name}
              className="rounded-lg transition-all shrink-0"
              style={{
                padding: 2,
                background: i === active ? 'linear-gradient(135deg, #4E9CFF, #FF4DB8)' : 'transparent',
                opacity: i === active ? 1 : 0.6,
              }}
            >
              <ScaledPage width={THUMB_W}>
                <PosterPageFrame brand={posterBrand} design={d} />
              </ScaledPage>
            </button>
          ))}
        </div>

        {/* Download */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-1">
          <p className="text-sm text-gray-600">
            {total} designs · branded with your Genie · print-ready PDF.
          </p>
          <div className="flex items-center gap-2 shrink-0">
            <Button
              type="button"
              variant="outline"
              onClick={() => openImmersive('grid')}
              className="border-2 hover:bg-gray-50"
              style={{ borderColor: `${brand}55`, color: brand }}
            >
              <Maximize2 className="w-4 h-4 mr-2" />
              View all designs
            </Button>
            <Button
              onClick={handleDownload}
              disabled={isDownloading}
              className="text-white border-0"
              style={{ background: `linear-gradient(135deg, ${brand}, #FF4DB8)` }}
            >
              {isDownloading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Building…
                </>
              ) : (
                <>
                  <Download className="w-4 h-4 mr-2" />
                  Download PDF
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Jump to the print order cart, pinned near the top of the page */}
        <a
          href="#print-order"
          className="rounded-xl border border-dashed px-4 py-3 flex items-center justify-between gap-3 hover:border-solid transition-colors"
          style={{ borderColor: `${brand}40`, background: `${brand}08` }}
        >
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: `${brand}15` }}>
              <Package className="w-5 h-5" style={{ color: brand }} />
            </div>
            <div className="min-w-0">
              <div className="text-sm font-semibold text-gray-900">Order printed posters</div>
              <div className="text-xs text-gray-600 truncate">
                Add this to your print order up top — pick quantities and send it to your printer.
              </div>
            </div>
          </div>
          <ChevronRight className="w-4 h-4 shrink-0" style={{ color: brand }} />
        </a>
      </div>

      <PosterImmersiveViewer
        open={viewerOpen}
        onClose={() => setViewerOpen(false)}
        designs={POSTER_DESIGNS}
        brand={posterBrand}
        brandColor={brand}
        initialIndex={active}
        initialMode={viewerMode}
        onDownload={handleDownload}
        isDownloading={isDownloading}
        onAddToPrintOrder={(designs: PosterDesign[], quantity: number) => addDesigns(designs, quantity)}
        isAddingToPrintOrder={isAdding}
      />
    </OverviewSectionCard>
  );
};
